import { ProfessionalMeteringInfo } from '@common/credits/ProfessionalEdition/types';
import { useProfessionalEditionCreditsContext } from '@common/credits/ProfessionalEdition/useProfessionalEditionCreditsContext';
import { isFreePlan } from '@common/credits/ProfessionalEdition/utils';
import { EntityEvents } from '@common/user-analytics';
import { formatUpdatedAt } from '@pages/UserSettings/Billing/ProfessionalEdition/utils';
import { Marker } from '@pages/UserSettings/components/Marker';
import { BellAIcon, ChevronDownIcon, ChevronRightIcon, SlashCircleAIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import {
  AccordionContainer,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  ProgressBar,
  Stack,
  Table,
  Tooltip
} from '@prophecy/ui-v3';
import { Text } from '@prophecy/ui-v3/Typography/Text';
import { roundToDecimals } from '@prophecy/utils/number';
import { useMixPanel } from 'frontend/core/src/context/mixpanel/context';
import { useUser } from 'frontend/core/src/context/user';
import { useState } from 'react';
import styled from 'styled-components';

import { ManageMonthlySpendingDialog } from './ManageMonthlySpendingDialog';

const MARKER_ICON_HEIGHT = 20;
const MARKER_ICON_PADDING = 8;
const MARKER_ICON_BORDER = 1;
const PROGRESS_BAR_HEIGHT = 20;
const PROGRESS_BAR_MARGIN = 16;

const CREDIT_USAGE_TOOLTIP_PLACEMENT_OFFSET = -10;

const transformMeteringInfo = (meteringInfo: ProfessionalMeteringInfo) => {
  const {
    creditsBreakdown,
    usageLimit = {
      alertAmount: 0,
      budgetAmount: 0
    }
  } = meteringInfo;

  return {
    serviceCreditsAllocated: meteringInfo.serviceCreditsAllocated,
    serviceCreditsConsumed: meteringInfo.serviceCreditsConsumed,
    pricePerCredit: meteringInfo.pricePerCredit,
    activeUsers: meteringInfo.activatedUsers,
    teamId: meteringInfo.teamID,
    teamName: meteringInfo.teamName,
    updatedAt: meteringInfo.updated_at,
    userId: meteringInfo.userID,
    usageLimit: {
      alert: {
        amount: usageLimit.alertAmount,
        credits: usageLimit.alertAmount ? roundToDecimals(usageLimit.alertAmount / meteringInfo.pricePerCredit) : 0
      },
      budget: {
        amount: usageLimit.budgetAmount,
        credits: usageLimit.budgetAmount ? roundToDecimals(usageLimit.budgetAmount / meteringInfo.pricePerCredit) : 0
      }
    },
    breakdown: {
      ai: {
        used: creditsBreakdown.aiRequestsCredits,
        color: `${theme.colors.brand600}`,
        creditCostPerUnit: meteringInfo.creditsCostPerAIRequest,
        label: 'AI Requests',
        unit: 'request'
      },
      compute: {
        used: creditsBreakdown.computeCredits,
        color: `${theme.colors.greenLight500}`,
        creditCostPerUnit: meteringInfo.creditsCostPerCPUHour,
        label: 'Compute',
        unit: 'CPU hour'
      },
      egress: {
        used: creditsBreakdown.dataEgressedCredits,
        color: `${theme.colors.orange500}`,
        creditCostPerUnit: meteringInfo.creditsCostPerGBEgressed,
        label: 'Data Egressed',
        unit: 'GB'
      },
      storage: {
        used: creditsBreakdown.dataStoredCredits,
        color: `${theme.colors.yellow500}`,
        creditCostPerUnit: meteringInfo.creditsCostPerGBStoredPerMonth,
        label: 'Data Storage',
        unit: 'GB'
      }
    }
  };
};

const computeIconCenterOffset = () => {
  const iconContainerHeight = MARKER_ICON_HEIGHT + MARKER_ICON_PADDING * 2 + MARKER_ICON_BORDER * 2;
  const progressBarCenter = PROGRESS_BAR_HEIGHT / 2 + PROGRESS_BAR_MARGIN;
  const iconCenter = iconContainerHeight / 2;
  const iconCenterOffset = progressBarCenter - iconCenter;
  return iconCenterOffset;
};

export const CreditUsage = () => {
  const { meteringInfo, error } = useProfessionalEditionCreditsContext();
  const { user } = useUser();
  const creditUsage = transformMeteringInfo(meteringInfo);

  const [isResourceUsageOpen, setIsResourceUsageOpen] = useState(false);

  const [isManageMonthlySpendingDialogOpen, setIsManageMonthlySpendingDialogOpen] = useState(false);

  const { track } = useMixPanel();

  // Alert/budget trigger once service usage passes the included allocation, so mark them from that baseline
  const usageAlertThreshold = creditUsage.serviceCreditsAllocated + creditUsage.usageLimit.alert.credits;
  const usageBudgetThreshold = creditUsage.serviceCreditsAllocated + creditUsage.usageLimit.budget.credits;
  // Floor at 1 so a not-yet-provisioned team (everything 0) doesn't divide by zero into NaN% widths
  const baseCredits = Math.max(
    1,
    creditUsage.serviceCreditsAllocated,
    creditUsage.serviceCreditsConsumed,
    usageAlertThreshold,
    usageBudgetThreshold
  );
  const usageLimitMarkerPosition = (usageAlertThreshold / baseCredits) * 100;
  const usageBudgetMarkerPosition = (usageBudgetThreshold / baseCredits) * 100;
  const canManageSpending = !isFreePlan(meteringInfo.plan) && creditUsage.userId === user.id;
  const iconCenterOffset = computeIconCenterOffset();
  if (!creditUsage) return null;

  return (
    <CreditUsageContainer gap={theme.spaces.x12}>
      <Stack direction='horizontal' align='space-between'>
        <Stack direction='vertical' align='center' gap={theme.spaces.x6}>
          <CreditUsageText level='sm' weight={theme.fontWeight.medium}>
            Credit Usage
          </CreditUsageText>
          {error && (
            <Text level='xs' tone={theme.colors.error500}>
              {error.message}
            </Text>
          )}
          {!error && (
            <Text level='xs' tone={theme.colors.gray700}>
              Last Updated: {formatUpdatedAt(creditUsage.updatedAt)}
            </Text>
          )}
        </Stack>
        {canManageSpending && (
          <Button
            variant='secondary'
            size='s'
            data-test-id='manage-monthly-spending-button'
            onClick={() => {
              track(EntityEvents.settings.billing.manageMonthlySpending, {
                teamId: creditUsage.teamId,
                trigger: 'creditUsage'
              });
              setIsManageMonthlySpendingDialogOpen(true);
            }}>
            Manage monthly spending
          </Button>
        )}
      </Stack>
      {!error && (
        <>
          <ProgressBarContainer direction='horizontal'>
            {Object.entries(creditUsage.breakdown).map(([key, value], index) => (
              <Tooltip
                title={
                  <Stack direction='vertical' gap={theme.spaces.x4}>
                    <Text level='xs'>
                      {value.label}: {roundToDecimals(value.used)} credits consumed
                    </Text>
                    <Text level='xs'>
                      Cost: {value.creditCostPerUnit} credits / {value.unit}
                    </Text>
                  </Stack>
                }
                key={key}
                placement='bottom'
                placementOffset={CREDIT_USAGE_TOOLTIP_PLACEMENT_OFFSET}>
                <ProgressBarWrapper data-test-id={`credit-usage-segment-${key}`} total={baseCredits} used={value.used}>
                  <StyledProgressBar
                    color={value.color}
                    usage={value.used}
                    variant='success'
                    hasLeftBorder={index === 0}
                    hasRightBorder={index === Object.entries(creditUsage.breakdown).length - 1}
                  />
                </ProgressBarWrapper>
              </Tooltip>
            ))}
            <ProgressBarWrapper
              data-test-id='credit-usage-remaining-segment'
              total={baseCredits}
              used={baseCredits - creditUsage.serviceCreditsConsumed}>
              <StyledProgressBar
                color={theme.colors.gray200}
                usage={baseCredits - creditUsage.serviceCreditsConsumed}
                variant='success'
                hasLeftBorder={false}
                hasRightBorder={false}
              />
            </ProgressBarWrapper>
            {creditUsage.usageLimit.alert.credits > 0 && (
              <Marker
                hideLines={true}
                position={usageLimitMarkerPosition}
                color={theme.colors.warning500}
                icon={<BellAIcon color={theme.colors.warning600} type={'default'} />}
                tooltip={`Usage alert: $${creditUsage.usageLimit.alert.amount}`}
                iconCenterOffset={iconCenterOffset}
              />
            )}
            {creditUsage.usageLimit.budget.credits > 0 && (
              <Marker
                hideLines={true}
                position={usageBudgetMarkerPosition}
                color={theme.colors.error500}
                icon={<SlashCircleAIcon color={theme.colors.error500} type={'default'} />}
                tooltip={`Usage budget: $${creditUsage.usageLimit.budget.amount}`}
                iconCenterOffset={iconCenterOffset}
              />
            )}
          </ProgressBarContainer>

          <AccordionContainer
            type='single'
            collapsible
            onValueChange={(value) => setIsResourceUsageOpen(value === 'credit-breakdown')}>
            <AccordionItem value='credit-breakdown'>
              <StyledAccordionTrigger>
                <Stack direction='horizontal' align='center' gap={theme.spaces.x6}>
                  <Text level='sm' weight={theme.fontWeight.semiBold} tone={theme.colors.brand500}>
                    View detailed resource usage
                  </Text>
                  {isResourceUsageOpen ? (
                    <ChevronDownIcon
                      size='s'
                      type='default'
                      color={theme.colors.gray600}
                      fontWeight={theme.fontWeight.semiBold}
                    />
                  ) : (
                    <ChevronRightIcon
                      size='s'
                      type='default'
                      color={theme.colors.gray600}
                      fontWeight={theme.fontWeight.semiBold}
                    />
                  )}
                </Stack>
              </StyledAccordionTrigger>
              <AccordionContent>
                <CreditSummaryTable
                  data-test-id='credit-summary-table'
                  columns={[
                    {
                      key: 'name',
                      title: <NameHeader level='xs'>Name</NameHeader>,
                      dataIndex: 'name',
                      render: (record: { color: string; name: number; text: string }) => (
                        <Stack direction='horizontal' gap={theme.spaces.x6}>
                          <ResourceBadge color={record.color} />
                          <Text level='sm' tone={theme.colors.gray900}>
                            {record.name}
                          </Text>
                        </Stack>
                      )
                    },
                    {
                      key: 'creditsUsage',
                      title: (
                        <Text level='xs' tone={theme.colors.gray600} weight={theme.fontWeight.medium}>
                          Credits Usage
                        </Text>
                      ),
                      dataIndex: 'creditsUsage',
                      render: (text: string) => (
                        <Text level='sm' tone={theme.colors.gray700}>
                          {text}
                        </Text>
                      )
                    },
                    {
                      key: 'unitPrice',
                      title: (
                        <Text level='xs' tone={theme.colors.gray600} weight={theme.fontWeight.medium}>
                          Unit Price (In Credits)
                        </Text>
                      ),
                      dataIndex: 'unitPrice',
                      render: (text: string) => (
                        <Text level='sm' tone={theme.colors.gray700}>
                          {text}
                        </Text>
                      )
                    }
                  ]}
                  dataSource={Object.entries(creditUsage.breakdown).map(([key, value]) => ({
                    key,
                    name: { name: value.label, color: value.color },
                    creditsUsage: value.used,
                    unitPrice: `${value.creditCostPerUnit} / ${value.unit}`
                  }))}
                  rowKey='key'
                />
              </AccordionContent>
            </AccordionItem>
          </AccordionContainer>
        </>
      )}
      {isManageMonthlySpendingDialogOpen && (
        <ManageMonthlySpendingDialog onClose={() => setIsManageMonthlySpendingDialogOpen(false)} />
      )}
    </CreditUsageContainer>
  );
};

const CreditUsageText = styled(Text)`
  color: ${theme.colors.gray900};
  font-weight: ${theme.fontWeight.medium};
`;

const StyledAccordionTrigger = styled(AccordionTrigger)`
  background-color: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const ProgressBarWrapper = styled.div<{ total: number; used: number }>`
  width: ${({ total, used }) => `${(used / total) * 100}%`};
`;

const StyledProgressBar = styled(ProgressBar)<{
  color: string;
  usage: number;
  hasLeftBorder: boolean;
  hasRightBorder: boolean;
}>`
  margin: ${theme.spaces.x16} 0;
  background-color: ${theme.colors.gray200};
  height: 20px;
  cursor: pointer;
  width: 100%;

  & > div {
    background-color: ${({ color }) => color};
    height: 20px;
    border-radius: 0;
    ${({ hasLeftBorder }) => hasLeftBorder && 'border-radius: 4px 0 0 4px;'}
    ${({ hasRightBorder }) => hasRightBorder && 'border-radius: 0 4px 4px 0;'}
  }
`;

const CreditSummaryTable = styled(Table)`
  margin-top: ${theme.spaces.x40};
`;

const NameHeader = styled(Text)`
  color: ${theme.colors.gray600};
  font-weight: ${theme.fontWeight.medium};
  margin-left: ${theme.spaces.x40};
`;

const ResourceBadge = styled.div<{ color: string }>`
  margin-left: ${theme.spaces.x8};
  margin-right: ${theme.spaces.x16};
  background-color: ${({ color }) => color};
  align-self: center;
  border-radius: 2px;
  width: ${theme.sizes.x12};
  height: ${theme.sizes.x12};
`;

const ProgressBarContainer = styled(Stack)`
  position: relative;
`;

const CreditUsageContainer = styled(Stack)`
  background-color: ${theme.colors.white};
  padding: ${theme.spaces.x16};
  border-radius: ${theme.spaces.x8};
  border: 1px solid ${theme.colors.gray200};
`;
